import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Loader2, RefreshCw, Upload } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { borrowerService } from "@/services/borrowers";
import type { BorrowerDetail, IntakeIssue, IntakeIssueDocument } from "@/types/borrower";
import { DOC_TYPE_LABELS, DOC_TYPES, DocType, TrackedFileDocument } from "@/types/statement";

interface IntakeStatusBannerProps {
  borrower: BorrowerDetail;
  /** Called to refetch the borrower after a state change. */
  onChanged: () => void;
}

const POLL_INTERVAL_MS = 5000;

/** Friendly, action-oriented copy for each readiness issue code. */
const ISSUE_COPY: Record<string, (issue: IntakeIssue) => string> = {
  missing_company_name: () => "Company name is missing.",
  missing_owner_name: () => "Owner name is missing.",
  missing_ein: () => "EIN is missing.",
  no_bank_statements: () =>
    "No bank statements are available on this application. Classify at least one document as a Bank Statement below, or upload statements.",
  unreadable_document: (issue) =>
    `${issue.count ?? 1} document(s) couldn't be read — re-upload a clear copy.`,
  all_attachments_unknown: () =>
    "No recognizable documents were found — classify documents below or upload statements.",
  possible_duplicate: (issue) =>
    `Possible duplicate: ${issue.count ?? 1} other application(s) share this EIN.`,
  intake_processing_error: () => "Processing hit an error — try reprocessing.",
  intake_timeout: () => "Processing timed out — try reprocessing.",
};

/** Codes that hold analysis; owner/EIN are soft and do not block financials. */
const BLOCKING_ISSUE_CODES = new Set(["missing_company_name", "no_bank_statements"]);

const SOFT_IDENTITY_CODES = new Set(["missing_owner_name", "missing_ein"]);

const DOCUMENT_CODES = new Set([
  "no_bank_statements",
  "unreadable_document",
  "all_attachments_unknown",
]);

const describeIssue = (issue: IntakeIssue): string => {
  const fn = ISSUE_COPY[issue.code];
  return fn ? fn(issue) : issue.code.replace(/_/g, " ");
};

const asDocType = (value: string | null | undefined): DocType =>
  DOC_TYPES.includes(value as DocType) ? (value as DocType) : "unknown";

const IntakeStatusBanner: React.FC<IntakeStatusBannerProps> = ({ borrower, onChanged }) => {
  const intakeStatus = borrower.intake_status;
  const issues = useMemo(() => borrower.intake_issues ?? [], [borrower.intake_issues]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [docPrompt, setDocPrompt] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const codes = useMemo(() => new Set(issues.map((i) => i.code)), [issues]);
  const missingOwnerField = !(borrower.owner_name ?? "").trim();
  const missingEinField = !(borrower.ein ?? "").trim();
  // Only company is blocking; owner/EIN stay editable in needs_input remediation
  // but do not get their own top-of-page banner once analysis is ready.
  const showCompany = codes.has("missing_company_name");
  const showOwner = codes.has("missing_owner_name") || missingOwnerField;
  const showEin = codes.has("missing_ein") || missingEinField;
  const showFields = showCompany || showOwner || showEin;

  const documentsFromIssue = useMemo(() => {
    const noStmt = issues.find((i) => i.code === "no_bank_statements");
    return (noStmt?.documents ?? []) as IntakeIssueDocument[];
  }, [issues]);

  const documents: Array<{ id: number; filename: string; doc_type: DocType }> = useMemo(() => {
    const fromBorrower = (borrower.documents ?? []) as TrackedFileDocument[];
    if (fromBorrower.length > 0) {
      return fromBorrower.map((d) => ({
        id: d.id,
        filename: d.filename,
        doc_type: asDocType(d.doc_type),
      }));
    }
    return documentsFromIssue.map((d) => ({
      id: d.id,
      filename: d.filename,
      doc_type: asDocType(d.doc_type),
    }));
  }, [borrower.documents, documentsFromIssue]);

  const [companyName, setCompanyName] = useState(borrower.company_name ?? "");
  const [ownerName, setOwnerName] = useState(borrower.owner_name ?? "");
  const [ein, setEin] = useState(borrower.ein ?? "");
  const [docTypes, setDocTypes] = useState<Record<number, DocType>>({});

  useEffect(() => {
    setCompanyName(borrower.company_name ?? "");
    setOwnerName(borrower.owner_name ?? "");
    setEin(borrower.ein ?? "");
  }, [borrower.company_name, borrower.owner_name, borrower.ein]);

  useEffect(() => {
    const next: Record<number, DocType> = {};
    for (const d of documents) {
      next[d.id] = d.doc_type;
    }
    setDocTypes(next);
  }, [documents]);

  // Poll while processing so the UI advances to ready/needs_input on its own.
  useEffect(() => {
    if (intakeStatus !== "processing") return;
    const t = setInterval(onChanged, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [intakeStatus, onChanged]);

  const handleReprocess = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await borrowerService.reprocessIntake(borrower.id);
      if (res.action === "needs_documents") {
        setDocPrompt(true);
        setMessage("Upload or classify documents below, then save.");
      } else {
        setDocPrompt(false);
        onChanged();
      }
    } catch {
      setMessage("Couldn't start reprocessing. Try again.");
    } finally {
      setBusy(false);
    }
  }, [borrower.id, onChanged]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length === 0) return;
      setBusy(true);
      setMessage(null);
      try {
        await borrowerService.uploadStatements(borrower.id, files);
        setDocPrompt(false);
        onChanged();
      } catch {
        setMessage("Upload failed. Try again.");
      } finally {
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [borrower.id, onChanged]
  );

  const handleSaveRemediation = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      const updates = documents.map((d) => ({
        file_id: d.id,
        doc_type: docTypes[d.id] ?? d.doc_type,
      }));
      const payload: {
        updates: { file_id: number; doc_type: string }[];
        company_name?: string;
        owner_name?: string;
        ein?: string;
      } = { updates };
      if (showCompany) payload.company_name = companyName.trim();
      if (showOwner) payload.owner_name = ownerName.trim();
      if (showEin) payload.ein = ein.trim();

      await borrowerService.updateDocumentTypes(borrower.id, payload);
      onChanged();
    } catch {
      setMessage("Couldn't save. Try again.");
    } finally {
      setBusy(false);
    }
  }, [
    borrower.id,
    companyName,
    documents,
    docTypes,
    ein,
    onChanged,
    ownerName,
    showCompany,
    showEin,
    showOwner,
  ]);

  if (!intakeStatus) return null;

  // Ready means financial analysis can proceed — do not surface a soft identity
  // banner; owner/EIN can be filled from the normal application fields later.
  if (intakeStatus === "ready") return null;

  if (intakeStatus === "processing") {
    return (
      <Card className="border-primary/20 bg-primary/5 p-6 shadow-none">
        <div className="flex items-start gap-3">
          <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-primary" />
          <div>
            <h2 className="text-lg font-semibold">Processing application…</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              We're classifying documents and checking readiness. This page updates automatically.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  // needs_input or processing_error
  const blocking = issues.filter(
    (i) =>
      i.code !== "possible_duplicate" &&
      !SOFT_IDENTITY_CODES.has(i.code) &&
      (BLOCKING_ISSUE_CODES.has(i.code) ||
        i.code === "unreadable_document" ||
        i.code === "all_attachments_unknown" ||
        i.code.startsWith("intake_"))
  );
  const hasDocIssue = blocking.some((i) => DOCUMENT_CODES.has(i.code));
  const showEditor = borrower.app_status === "needs_input" && (showFields || documents.length > 0);

  return (
    <Card className="border-amber-400/40 bg-amber-50 p-6 shadow-none dark:bg-amber-950/20">
      <div className="mb-3 flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1">
          <h2 className="text-lg font-semibold">
            {intakeStatus === "processing_error"
              ? "Processing error — needs attention"
              : "More information needed"}
          </h2>
          <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-muted-foreground">
            {blocking.length > 0 ? (
              blocking.map((issue, idx) => (
                <li key={`${issue.code}-${idx}`}>
                  {describeIssue(issue)}
                  {issue.filenames && issue.filenames.length > 0 && (
                    <ul className="mt-1 list-inside list-disc space-y-0.5 pl-4 text-xs">
                      {issue.filenames.map((f) => (
                        <li key={f}>{f}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))
            ) : (
              <li>Reprocess to re-evaluate this application.</li>
            )}
          </ul>
          {(missingOwnerField || missingEinField) && (
            <p className="mt-2 text-sm text-muted-foreground">
              Owner name and EIN are optional for analysis — add them when available for litigation
              and default-history checks.
            </p>
          )}
          {message && <p className="mt-2 text-sm font-medium text-amber-700">{message}</p>}
        </div>
      </div>

      {showEditor && (
        <div className="mb-4 space-y-4 rounded-md border border-amber-400/30 bg-background/60 p-4">
          {showFields && (
            <div className="space-y-3">
              <p className="text-sm font-medium">Application fields</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {showCompany && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="intake-company">
                      Company Name
                    </label>
                    <Input
                      id="intake-company"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                )}
                {showOwner && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="intake-owner">
                      Owner Name
                    </label>
                    <Input
                      id="intake-owner"
                      value={ownerName}
                      onChange={(e) => setOwnerName(e.target.value)}
                      disabled={busy}
                    />
                  </div>
                )}
                {showEin && (
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground" htmlFor="intake-ein">
                      EIN
                    </label>
                    <Input
                      id="intake-ein"
                      value={ein}
                      onChange={(e) => setEin(e.target.value)}
                      disabled={busy}
                      placeholder="XX-XXXXXXX"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {documents.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Classify documents</p>
              <ul className="space-y-2">
                {documents.map((doc) => (
                  <li key={doc.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="min-w-0 max-w-[240px] flex-1 truncate" title={doc.filename}>
                      {doc.filename}
                    </span>
                    <Select
                      value={docTypes[doc.id] ?? doc.doc_type}
                      onValueChange={(v) =>
                        setDocTypes((prev) => ({ ...prev, [doc.id]: v as DocType }))
                      }
                      disabled={busy}
                    >
                      <SelectTrigger size="sm" className="w-[220px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOC_TYPES.map((dt) => (
                          <SelectItem key={dt} value={dt} className="text-xs">
                            {DOC_TYPE_LABELS[dt]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={handleSaveRemediation}
              disabled={busy}
              title="Save and re-evaluate readiness"
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-1.5 h-4 w-4" />
              )}
              Save &amp; continue
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!showEditor && (
          <Button onClick={handleReprocess} disabled={busy} size="sm">
            {busy ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-1.5 h-4 w-4" />
            )}
            Reprocess
          </Button>
        )}

        {(docPrompt || hasDocIssue || documents.length === 0) && (
          <>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-1.5 h-4 w-4" />
              Upload documents
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.csv,.xlsx,.xls,.ofx,.jpg,.jpeg,.png"
              className="hidden"
              onChange={handleUpload}
            />
          </>
        )}
      </div>
    </Card>
  );
};

export default IntakeStatusBanner;
